/**
 * Runtime configuration loader.
 *
 * In deployed environments, config is loaded from /config.json
 * (injected by CDK's BucketDeployment). In local dev, falls back
 * to Vite environment variables.
 */

/**
 * Non-secret Stripe tier + price display metadata for the plan-selection UI.
 * Mirrors the shape the infra frontend-stack emits into config.json
 * (packages/infra/src/stacks/frontend-stack.ts `stripePlans` prop). Carries only
 * public identifiers (Stripe price IDs / lookup keys) and display names — no
 * secret material (Req 9.4, 9.5).
 */
export interface StripePlanConfig {
  /** Internal tier key: 'trade_basic' | 'trade_execution' | 'trade_discovery'. */
  tier: string;
  /** Human-readable tier name, e.g. 'Trade Basic'. */
  displayName: string;
  /** Monthly cadence identifiers (non-secret). */
  monthly: { lookupKey: string; priceId: string };
  /** Annual cadence identifiers (non-secret). */
  annual: { lookupKey: string; priceId: string };
}

export interface RuntimeConfig {
  apiBaseUrl: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  // New — Hosted UI / OAuth (Req 6, 10.8). Optional so existing email/password-only envs still load.
  cognitoDomain?: string; // per-env Hosted UI domain, e.g. coveredcalls-dev.auth.ap-southeast-2.amazoncognito.com
  oauthScopes?: string[]; // exactly ['openid','email','profile']
  redirectSignIn?: string; // Callback_URL for this origin
  redirectSignOut?: string; // Sign_Out_URL for this origin
  enabledFederatedProviders?: string[]; // which federated providers are enabled for this env (e.g. ['google'])
  // New — Version_Footer (Req 3.1). Optional so older config.json / existing consumers still load.
  version?: string; // Application_Version, e.g. '0.1.0'
  environment?: string; // deploy target env name: 'dev' | 'test' | 'prod'
  // New — Stripe payments (Req 9.4). Non-secret display config only; optional so envs without Stripe still load.
  stripePublishableKey?: string; // non-secret Stripe publishable key (pk_test_.../pk_live_...)
  stripePlans?: StripePlanConfig[]; // non-secret tier + price display metadata for the plan-selection UI
  // New — public marketing site URL. Optional so older config.json / existing consumers still load.
  marketingUrl?: string; // public marketing site URL, e.g. https://www.dev.coveredcall.finance
  // New — public privacy information URL. Derived by the infra from marketingUrl
  // (`<marketingUrl>/#privacy`) and delivered via config.json. Optional so older
  // config.json / existing consumers still load; consumers fall back via getPrivacyUrl().
  privacyUrl?: string; // public privacy info URL, e.g. https://www.dev.coveredcall.finance/#privacy
}

/** Fallback marketing site URL for consumers that don't want to null-check. */
export const DEFAULT_MARKETING_URL = 'https://www.coveredcall.finance';

/**
 * Fallback privacy information URL for consumers that don't want to null-check.
 * The privacy info lives at the `#privacy` fragment of the marketing site, so
 * this is kept consistent with DEFAULT_MARKETING_URL as its single source.
 */
export const DEFAULT_PRIVACY_URL = `${DEFAULT_MARKETING_URL}/#privacy`;

let cachedConfig: RuntimeConfig | null = null;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cachedConfig) return cachedConfig;

  // Try loading from config.json (deployed environment)
  try {
    const response = await fetch('/config.json');
    if (response.ok) {
      const config = await response.json();
      if (config.apiBaseUrl && config.cognitoUserPoolId && config.cognitoClientId) {
        cachedConfig = config;
        return config;
      }
    }
  } catch {
    // config.json not available — fall through to env vars
  }

  // Fallback to Vite environment variables (local dev)
  cachedConfig = {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
    cognitoUserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
    cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
    cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN || undefined,
    oauthScopes: ['openid', 'email', 'profile'],
    redirectSignIn: import.meta.env.VITE_OAUTH_REDIRECT_SIGNIN || 'http://localhost:5173/',
    redirectSignOut: import.meta.env.VITE_OAUTH_REDIRECT_SIGNOUT || 'http://localhost:5173/',
    enabledFederatedProviders:
      import.meta.env.VITE_ENABLED_FEDERATED_PROVIDERS?.split(',')
        .map((s: string) => s.trim())
        .filter(Boolean) ?? ['google'],
    // Version_Footer local-dev fallback (Req 4.1–4.3). A Vite `define` injecting the real
    // root package.json version into VITE_APP_VERSION can be wired later; '0.0.0' is the
    // documented acceptable local value until then.
    version: import.meta.env.VITE_APP_VERSION || '0.0.0',
    environment: 'dev',
    marketingUrl: import.meta.env.VITE_MARKETING_URL || DEFAULT_MARKETING_URL,
    privacyUrl: import.meta.env.VITE_PRIVACY_URL || DEFAULT_PRIVACY_URL,
  };

  return cachedConfig;
}

export function getRuntimeConfig(): RuntimeConfig | null {
  return cachedConfig;
}

/**
 * Resolve the privacy information URL for the current environment.
 *
 * Prefers the runtime `privacyUrl` (derived per-env by the infra from the
 * marketing domain), then derives one from a runtime `marketingUrl` if only that
 * is present, and finally falls back to DEFAULT_PRIVACY_URL. Centralizes what was
 * previously a hardcoded constant duplicated across several components.
 */
export function getPrivacyUrl(): string {
  const cfg = getRuntimeConfig();
  if (cfg?.privacyUrl) return cfg.privacyUrl;
  if (cfg?.marketingUrl) return `${cfg.marketingUrl.replace(/\/+$/, '')}/#privacy`;
  return DEFAULT_PRIVACY_URL;
}
